import {Component, EventEmitter, forwardRef, Input, OnInit, Output} from '@angular/core';
import {SubscribedComponent} from "../../subscribed.component";
import {ColDef, ColType} from "../../interfaces/colDef";
import {LeaveDialogComponent} from "../../dialogs/popup-dialog/leave-dialog.component";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {NbDialogService} from "@nebular/theme";
import {User} from "../../interfaces/user";
import {Globals} from "../../shared/Globals";
import moment from "moment";

export interface Holiday{
  created_at?: Date,
  updated_at?: Date,
  id: number,
  name?: string,
  start_on: Date|string,
  end_on: Date|string,
  dates?: {start: Date|string, end:Date|string},
  User_id?: number,
  block: boolean
}

@Component({
    selector: 'bst-leave-manager',
    templateUrl: './leave-manager.component.html',
    styleUrls: ['./leave-manager.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LeaveManagerComponent),
            multi: true
        }
    ],
    standalone: false
})
export class LeaveManagerComponent extends SubscribedComponent implements ControlValueAccessor{

  @Input() public readonly: boolean = false;
  @Input() public user: User;

  // #786: opt-in read-mode "Add leave" affordance. When set, a read-only manager
  // still offers an editable dialog and hands the result back to the parent via
  // quickAddSaved so it can persist a holidays-only patch (no full profile edit).
  @Input() public quickAdd: boolean = false;
  @Output() public quickAddSaved = new EventEmitter<Holiday[]>();

  public disabled:boolean = false;
  public holiday: Holiday[] = [];
  Globals = Globals;
  public readonly today = moment().set({hour: 0, minute: 0, second: 0, millisecond: 0}).toDate();

  // Function to call when the value changes.
  onChange = (value: Holiday[]) => {};
  // Function to call when the input is touched (when a star is clicked).
  onTouched = () => {};

  constructor(private dialogService: NbDialogService) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  public get lastHoliday(){
    if(this.holiday && this.holiday.length){
      let top: Holiday = null;
      for(let holiday of this.holiday){
        if(Globals.fromDateString(holiday.dates.end) < this.today){
          break;
        }
        top = holiday;
      }
      return top;
    }
    return null;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(holiday: Holiday[]): void {
    this.holiday = Array.from(holiday);
  }

  private leaveColDefs(): ColDef[] {
    return [
      {
        name: 'name',
        type: ColType.STRING,
        required: false,
        title: 'Leave comment',
        subtitle: 'leave comment',
        length: 100,
        filter: false,
        sort: false
      },
      {
        name: 'dates',
        type: ColType.RANGE,
        required: true,
        title: 'Dates',
        style: {
          minWidth: '250px'
        },
        subtitle: 'Enter date range',
        filter: false,
        sort: false
      }
    ];
  }

  // Copy the table-editor's range output onto the flat start_on/end_on/block
  // fields the rest of the app reads. Mutates and returns the same list.
  private normalizeLeave(value: Holiday[]): Holiday[] {
    (value || []).forEach(o => {
      o.start_on = o.dates.start;
      o.end_on = o.dates.end;
      o.block = true;
    });
    return value;
  }

  private openLeaveDialog(editable: boolean) {
    return this.dialogService.open<LeaveDialogComponent>(LeaveDialogComponent, {
      context: {
        componentContext: {
          disabled: !editable,
          standardColumns: this.leaveColDefs(),
          title: 'Leave',
          subtitle: 'Leave',
          data: this.holiday ? this.holiday.map(o => {
            return {
              expanded: false,
              children: [],
              data: o
            };
          }) : []
        }
      }
    });
  }

  // A backdrop/ESC dismiss closes with undefined; Close always returns an array.
  async openLeave() {
    this.loading.next(true);
    this.subs.push(this.openLeaveDialog(!this.disabled && !this.readonly).onClose.subscribe((value: Holiday[]) => {
      if(value && !this.disabled && !this.readonly) {
        this.holiday = this.normalizeLeave(value);
        this.onChange(this.holiday);
      }
    }));
    this.loading.next(false);
  }

  // #786: read-mode quick add. Opens the editable dialog even though the field is
  // readonly, then hands the result to the parent to persist on its own (a
  // holidays-only patch). Guards against a backdrop/ESC dismiss (value undefined),
  // which would otherwise wipe all leave.
  async openLeaveQuick() {
    this.loading.next(true);
    this.subs.push(this.openLeaveDialog(true).onClose.subscribe((value: Holiday[]) => {
      if(!value) { return; }
      this.holiday = this.normalizeLeave(value);
      this.quickAddSaved.emit(this.holiday);
    }));
    this.loading.next(false);
  }

}
